import React, { useState, useEffect, useRef } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { fetchOrderItemLookups } from '~/redux/actions/admin-lookups-actions';
import { CLEAR_ADMIN_LOOKUPS_FILTERS } from '~/redux/actions/action-types';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';

// Components
import Table from './Table';
import AdvanceFilters from './Filters';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';

// MUI Components
import { Box, Grid, Container, Typography, useMediaQuery } from '@mui/material';

const Lookups = () => {
  const [isMounted, setIsMounted] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [barcode, setBarcode] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [openAdvanceFilters, setOpenAdvanceFilters] = useState(false);
  const childRef = useRef();

  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const dispatch = useDispatch();

  const applyFilters = useSelector((state) => state.adminLookupsReducers.applyFilters);
  const filterSize = useSelector((state) => state.adminLookupsReducers.filterSize);
  const filters = useSelector((state) => state.adminLookupsReducers.filters);

  const getLookups = () => {
    let payload = {
      pagination,
      barcode: barcode?.length ? barcode : null,
      applyFilters,
      filters: applyFilters ? filters : null,
    };
    dispatch(fetchOrderItemLookups(payload));
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 0 });
    setSearchApplied(!!barcode);
    getLookups();
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  useEffect(() => {
    setIsMounted(true);
    getLookups();
    return () => {
      dispatch({ type: CLEAR_ADMIN_LOOKUPS_FILTERS });
    };
  }, []);

  useEffect(() => {
    if (!barcode && isMounted) {
      setSearchApplied(false);
      getLookups();
    }
  }, [barcode]);

  useEffect(() => {
    if (isMounted) {
      getLookups();
    }
  }, [pagination.page, pagination.pageSize, filterSize, filters]);

  return (
    <Container maxWidth={maxWidth} className="olcAdminListPage">
      <Grid container className="olcAdminListToolbar">
        <Grid item lg={6} md={8} sm={12} xs={12} className="searchBtnWrapper">
          <Box className="searchWrapper">
            <OlcSearchInput
              type="text"
              value={barcode}
              searchApplied={searchApplied}
              onChange={(e) => setBarcode(e.target.value.trim())}
              onKeyDown={debouncedHandleKeyDown}
              placeholder={MESSAGES.ADMIN_DASHBOARD.LOOKUPS.SEARCH_PLACE_HOLDER}
              inputProps={{ 'aria-label': MESSAGES.ADMIN_DASHBOARD.LOOKUPS.SEARCH_PLACE_HOLDER }}
              onClear={() => setBarcode('')}
            />
            <Box className="filterBtnWrapper">
              <OlcButton variant="secondary" onClick={() => setOpenAdvanceFilters(true)}>
                Filters
              </OlcButton>
              {filterSize > 0 && <Typography className="filtersCounter">{filterSize}</Typography>}
            </Box>
            {filterSize > 0 && (
              <OlcButton
                variant="secondary"
                onClick={() => {
                  childRef.current.clearFilters();
                }}
                className="clearFilterBtn"
              >
                {MESSAGES.ADMIN_DASHBOARD.LOOKUPS.CLEAR_FILTER_BTN_TEXT}
              </OlcButton>
            )}
          </Box>
        </Grid>
      </Grid>
      <Table
        pagination={pagination}
        updatePagination={setPagination}
        searchApplied={searchApplied || filterSize > 0}
      />
      <AdvanceFilters
        open={openAdvanceFilters}
        handleCloseFilter={() => setOpenAdvanceFilters(false)}
        ref={childRef}
        updatePagination={setPagination}
      />
    </Container>
  );
};

export default Lookups;
